// ---------------------------------------------------------------------------
// DiCharts SDK – Widget (Orchestrator)
// ---------------------------------------------------------------------------

import { DiChart, createTickAggregator } from '../../dicharts-lib/dist/index.js';
import type { DiChartInstance, DiChartOptions, TickAggregator } from '../../dicharts-lib/dist/index.js';
import type {
  WidgetConfig,
  DiWidget,
  CandleStyle,
  OverlayConfig,
  SubPaneConfig,
  TickInput,
  ToolbarConfig,
} from './types';
import { INDICATORS, TIMEFRAMES } from './types';
import { injectStyles } from './ui/styles';
import {
  ICON_NAV_BACK, ICON_NAV_FORWARD, ICON_NAV_ZOOMIN, ICON_NAV_ZOOMOUT, ICON_NAV_RESET,
} from './ui/icons';
import { EventEmitter } from './events';
import type { WidgetEventMap, WidgetEventName } from './events';
import { buildToolbar } from './toolbar';
import type { ToolbarState } from './toolbar';
import {
  generateSecondaryData, setLayout, updatePrimaryBadge, syncOrderLines,
} from './layouts';
import type { LayoutId, SecondaryPane } from './layouts';

// ---------------------------------------------------------------------------
// DOM helpers
// ---------------------------------------------------------------------------

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  html?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html) e.innerHTML = html;
  return e;
}

function btn(cls: string, html: string, title?: string): HTMLButtonElement {
  const b = el('button', cls, html);
  b.type = 'button';
  if (title) b.title = title;
  return b;
}

// ---------------------------------------------------------------------------
// createWidget
// ---------------------------------------------------------------------------

export async function createWidget(
  target: string | HTMLElement,
  config: WidgetConfig = {},
): Promise<DiWidget> {
  injectStyles();

  const container = typeof target === 'string'
    ? document.querySelector<HTMLElement>(target)
    : target;
  if (!container) throw new Error(`DiCharts: container not found: ${target}`);

  // ── Resolve config ──
  const cfg = {
    theme: config.theme ?? 'dark',
    style: config.style ?? ('classic' as CandleStyle),
    volume: config.volume !== false,
    toolbar: config.toolbar !== false,
    chartNav: config.chartNav !== false,
    autoScroll: config.autoScroll !== false,
    data: config.data ?? [],
    volumes: config.volumes ?? [],
    overlays: config.overlays ?? [],
    subPanes: config.subPanes ?? [],
  };

  const tbConfig: ToolbarConfig = typeof config.toolbar === 'object'
    ? config.toolbar
    : { stylePicker: true, timeframes: true, indicators: true, layout: true, settings: true, fullscreen: true, themeToggle: true };

  const symbolName = config.symbol ?? '';

  // ── Event emitter ──
  const emitter = new EventEmitter();

  // ── State ──
  const state: ToolbarState = {
    currentTheme: cfg.theme,
    currentStyle: cfg.style,
    volumeEnabled: cfg.volume,
    activeIndicators: new Set<string>(),
    aggregator: null,
    currentTimeframeKey: config.streaming?.timeframe ?? '1m',
    showBuyLine: true,
    showTPLine: true,
    showSLLine: true,
  };

  let currentLayout: LayoutId = 'single';
  let secondaryPanes: SecondaryPane[] = [];
  let lastPrice = 0;
  let priceDirection: 'up' | 'down' = 'up';

  // Derive initial price from data
  if (cfg.data.length > 0) {
    lastPrice = cfg.data[cfg.data.length - 1][4];
  }

  // Initialize active indicators from config overlays/subPanes
  for (const o of cfg.overlays) {
    const match = INDICATORS.find(i => i.defaultConfig.id === o.id);
    if (match) state.activeIndicators.add(match.id);
  }
  for (const s of cfg.subPanes) {
    const match = INDICATORS.find(i => i.defaultConfig.id === s.id);
    if (match) state.activeIndicators.add(match.id);
  }

  // If streaming is configured, create aggregator
  if (config.streaming) {
    const tf = TIMEFRAMES.find(t => t.key === config.streaming!.timeframe);
    if (tf) state.aggregator = createTickAggregator(tf.intervalMs);
  }

  // ── Helper: build overlays/subPanes from active indicators ──
  function buildOverlays(): OverlayConfig[] {
    return INDICATORS
      .filter(i => i.category === 'overlay' && state.activeIndicators.has(i.id))
      .map(i => i.defaultConfig as OverlayConfig);
  }

  function buildSubPanes(): SubPaneConfig[] {
    return INDICATORS
      .filter(i => i.category === 'oscillator' && state.activeIndicators.has(i.id))
      .map(i => i.defaultConfig as SubPaneConfig);
  }

  function refreshChart() {
    chart.setOptions({
      overlays: buildOverlays(),
      subPanes: buildSubPanes(),
    });
  }

  // ── Build DOM ──
  const root = el('div', `dw-root${state.currentTheme === 'light' ? ' dw-light' : ''}`);
  let priceEl: HTMLElement | null = null;

  // ── Toolbar ──
  if (cfg.toolbar) {
    const tb = buildToolbar(root, tbConfig, state, {
      onStyleChange(style) {
        chart.setOptions({ candles: { style } });
        for (const p of secondaryPanes) p.chart.setOptions({ candles: { style } });
      },
      onTimeframeChange(tf) {
        const newData = generateSecondaryData(cfg.data, tf.intervalMs);
        chart.setData(newData.candles);
        chart.setVolumes(newData.volumes);
        if (newData.candles.length > 0) {
          lastPrice = newData.candles[newData.candles.length - 1][4];
        }
        syncOrderLines(chart, lastPrice, state.showBuyLine, state.showTPLine, state.showSLLine);
        updatePrimaryBadge(primaryBadge, currentLayout, state.currentTimeframeKey, symbolName);
        config.onTimeframeChange?.(tf);
      },
      onIndicatorToggle: refreshChart,
      onLayoutChange(layout) {
        handleLayoutChange(layout as LayoutId);
      },
      onVolumeToggle(enabled) {
        chart.setOptions({ volume: { enabled, data: cfg.volumes } });
      },
      onOrderLineChange() {
        syncOrderLines(chart, lastPrice, state.showBuyLine, state.showTPLine, state.showSLLine);
      },
      onThemeToggle() {
        chart.setOptions({ theme: state.currentTheme });
        for (const p of secondaryPanes) p.chart.setOptions({ theme: state.currentTheme });
      },
      onFullscreen(r) {
        if (!document.fullscreenElement) {
          r.requestFullscreen?.();
        } else {
          document.exitFullscreen?.();
        }
      },
    }, emitter, symbolName, config.streaming?.timeframe);

    root.appendChild(tb.toolbar);
    priceEl = tb.priceEl;

    if (priceEl && lastPrice > 0) {
      priceEl.textContent = `$${lastPrice.toFixed(2)}`;
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Chart area (grid container for multi-layout)
  // ══════════════════════════════════════════════════════════════════════════

  const chartArea = el('div', 'dw-chart-area');
  chartArea.dataset.layout = 'single';

  // Primary pane
  const primaryPane = el('div', 'dw-pane');

  // Chart nav controls
  if (cfg.chartNav) {
    const nav = el('div', 'dw-chart-nav');
    const navBtns: [string, string, () => void][] = [
      [ICON_NAV_BACK, 'Pan left', () => chart.panLeft()],
      [ICON_NAV_ZOOMIN, 'Zoom in', () => chart.zoomIn()],
      [ICON_NAV_ZOOMOUT, 'Zoom out', () => chart.zoomOut()],
      [ICON_NAV_RESET, 'Reset zoom', () => chart.resetZoom()],
      [ICON_NAV_FORWARD, 'Pan right', () => chart.panRight()],
    ];

    for (const [icon, title, handler] of navBtns) {
      const b = btn('dw-nav-btn', icon, title);
      b.addEventListener('click', handler);
      nav.appendChild(b);
    }

    primaryPane.appendChild(nav);
  }

  // Primary pane badge
  const primaryBadge = el('div', 'dw-pane-badge');
  primaryBadge.style.display = 'none';
  primaryPane.appendChild(primaryBadge);

  chartArea.appendChild(primaryPane);
  root.appendChild(chartArea);

  // Mount
  container.innerHTML = '';
  container.appendChild(root);

  // ══════════════════════════════════════════════════════════════════════════
  // Create chart
  // ══════════════════════════════════════════════════════════════════════════

  const chartOpts: DiChartOptions = {
    theme: state.currentTheme,
    candles: { data: cfg.data, style: state.currentStyle },
    volume: { enabled: state.volumeEnabled, data: cfg.volumes },
    overlays: buildOverlays(),
    subPanes: buildSubPanes(),
    autoScroll: cfg.autoScroll,
  };

  const chart = await DiChart.create(primaryPane, chartOpts);

  // Add initial order lines
  syncOrderLines(chart, lastPrice, state.showBuyLine, state.showTPLine, state.showSLLine);

  // Close sub-pane via X button
  primaryPane.addEventListener('dicharts:close-subpane', ((e: CustomEvent) => {
    const id = e.detail.id;
    const ind = INDICATORS.find(i => i.defaultConfig.id === id);
    if (ind) {
      state.activeIndicators.delete(ind.id);
      emitter.emit('indicatorRemove', ind.id);
      refreshChart();
      root.querySelectorAll('.dw-ind-row').forEach(row => {
        if ((row as HTMLElement).dataset.ind === ind.id) {
          row.classList.remove('dw-active');
        }
      });
    }
  }) as EventListener);

  // ── Layout change handler ──
  async function handleLayoutChange(layout: LayoutId) {
    if (layout === currentLayout) return;
    secondaryPanes = await setLayout(
      layout, chartArea, primaryBadge,
      state.currentTheme, state.currentStyle,
      state.currentTimeframeKey, symbolName,
      cfg.data, secondaryPanes,
    );
    currentLayout = layout;
    root.querySelectorAll('.dw-layout-opt').forEach(b => {
      b.classList.toggle('dw-active', (b as HTMLElement).dataset.layout === layout);
    });
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Widget API
  // ══════════════════════════════════════════════════════════════════════════

  const widget: DiWidget = {
    addTick(tick: TickInput) {
      // Update price display
      if (priceEl && tick.price > 0) {
        priceDirection = tick.price >= lastPrice ? 'up' : 'down';
        lastPrice = tick.price;
        priceEl.textContent = `$${lastPrice.toFixed(2)}`;
        priceEl.className = `dw-symbol-price dw-${priceDirection}`;
      }

      emitter.emit('tick', tick);

      if (!state.aggregator) return;
      const completed = state.aggregator.processTick({ timestamp: tick.timestamp, price: tick.price });
      const forming = state.aggregator.currentCandle();
      if (!forming) return;

      if (completed) {
        chart.appendCandles([completed]);
        chart.appendVolumes([tick.volume != null ? Math.round(tick.volume) : 0]);
      }
      chart.updateLastCandle(forming);
    },

    appendCandle(candle) {
      chart.appendCandles([candle]);
    },

    updateLastCandle(candle) {
      chart.updateLastCandle(candle);
    },

    appendVolume(volume) {
      chart.appendVolumes([volume]);
    },

    setData(candles) {
      cfg.data = candles;
      chart.setData(candles);
    },

    setVolumes(volumes) {
      cfg.volumes = volumes;
      chart.setVolumes(volumes);
    },

    setTheme(theme) {
      state.currentTheme = theme;
      root.classList.toggle('dw-light', theme === 'light');
      chart.setOptions({ theme });
      for (const p of secondaryPanes) p.chart.setOptions({ theme });
      emitter.emit('themeChange', theme);
    },

    setStyle(style) {
      state.currentStyle = style;
      chart.setOptions({ candles: { style } });
      for (const p of secondaryPanes) p.chart.setOptions({ candles: { style } });
      root.querySelectorAll('.dw-style-opt').forEach(b => {
        b.classList.toggle('dw-active', (b as HTMLElement).dataset.style === style);
      });
      emitter.emit('styleChange', style);
    },

    get chart() {
      return chart;
    },

    on<K extends WidgetEventName>(event: K, fn: (...args: WidgetEventMap[K]) => void) {
      emitter.on(event, fn);
    },

    off<K extends WidgetEventName>(event: K, fn: (...args: WidgetEventMap[K]) => void) {
      emitter.off(event, fn);
    },

    dispose() {
      for (const p of secondaryPanes) p.chart.dispose();
      secondaryPanes = [];
      chart.dispose();
      emitter.removeAllListeners();
      root.remove();
    },
  };

  // Fire onReady callback & event
  emitter.emit('ready');
  config.onReady?.(widget);

  return widget;
}
